import { useEffect, useState } from "react";
import { InlineMath } from "react-katex";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import PortalGlassLightbox from "../PortalGlassLightbox";
import Icon from "../../icons/Icon";
import { constructInputWithSpecialExpressionList, checkAnswerState } from "../../../utilization/CatchtivityUtilization";
import { IMCSAActivityMaterialProps } from "../../../properties/ActivityProperties";
import i18n from "../../../language/i18n";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

const MCSAActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
  revealMarkers,
}: IMCSAActivityMaterialProps) => {
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);

  useEffect(() => {
    if (showCorrectAnswer) {
      // Key it by the MATERIAL key (which for MCSA *is* the correct answer), the
      // shape every lookup below uses. `{ correctAnswer }` built a map under the
      // literal key "correctAnswer", so displayAnswerMap[materialKey] was always
      // undefined and the reveal only worked by falling through the not-picked
      // branch.
      const correctAnswer = retrieveCorrectAnswer();
      setDisplayAnswerMap({ [correctAnswer]: correctAnswer });
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap, materialMap]);

  const retrieveCorrectAnswer = () => {
    return Object.keys(materialMap)[0];
  };

  const correctAnswer = retrieveCorrectAnswer();
  // When the answer is locked (submitted, time exceeded, duel/contest already
  // answered) the learner still sees the full options view — the picked option
  // stays highlighted — but it is non-interactive (no answer change).
  const canAnswer = checkCanAnswerQuestion();
  // Once graded, `mc-correct` fills the radio dot of the CORRECT option whether or
  // not the learner chose it — so the control asserts a selection that may not be
  // the learner's. Ring their own pick on top of the verdict colour so "what I
  // answered" is readable independently of "what was correct". Suppressed for a
  // plain correct-answer reveal, where displayAnswerMap IS the key.
  // Mirrors TrueFalseActivityMaterialContent.
  const showLearnerMarks = Boolean(isPreview) && !showCorrectAnswer;
  // `|| {}` for the same reason the option list falls back to [] below: a
  // malformed activity must degrade, not white-screen. This runs ABOVE that
  // guard, so it cannot borrow it.
  //
  // "ANSWER_KEY" is constructActivityAnswerMap's UNTOUCHED placeholder (see
  // duelRevealUtil.parseDuelPick, which reads it the same way): a question the
  // learner skipped still carries it, so a bare `!= null` counted a blank as a
  // pick. That put an unanswered question on the grade-against-learner path,
  // where the key is drawn with no ✓ and a legend names "your answer" for an
  // answer that was never given.
  const hasLearnerChoice = Object.keys(materialMap).some((materialKey) => {
    const picked = (displayAnswerMap || {})[materialKey];
    return picked != null && picked !== "" && picked !== "ANSWER_KEY";
  });
  // ✓/✗ answer "was I right?" whenever there is a learner answer to grade — the
  // convention Matching/Grouping/Ordering/Dropdown already follow. A ✓ on the KEY
  // the learner never picked reads as "you were right". With no learner answer at
  // all this degrades to a bare key reveal, where ✓-on-key is unambiguous again.
  const gradeAgainstLearner = showLearnerMarks && hasLearnerChoice;
  const showLegend = gradeAgainstLearner;

  // Multiplayer answer reveal: pins each player's name on the option they picked.
  const renderRevealTags = (optionValue: string) => {
    const markers = revealMarkers?.[optionValue];
    if (!markers || markers.length === 0) return null;
    return (
      <span className="mc-picktags">
        {markers.map((marker, markerIndex) => (
          <span
            key={markerIndex}
            className={`mc-picktag ${
              marker.correct ? "mc-picktag--ok" : "mc-picktag--no"
            }`}
          >
            {marker.name}
          </span>
        ))}
      </span>
    );
  };

  return (<div className="mc-root">{Object.keys(materialMap).map((materialKey, index) => {
    // Defensive: a malformed activity can provide a non-array option list;
    // fall back to [] so bad data degrades gracefully instead of throwing
    // "materialMap[materialKey].map is not a function" and white-screening.
    const options = Array.isArray(materialMap[materialKey])
      ? materialMap[materialKey]
      : [];
    return (
      <div className="mc-section" key={index}>
        <div className="mc-panel">
          <div className="mc-scroll">
          {contentMap.type === "IMAGE" ? (
              // Image options — 2-col card grid with a radio-dot label row. The
              // whole card (image included) picks the option; the corner button
              // zooms into the glass lightbox (design .spv-imgopts).
              <div
                className={`mc-imgopts${canAnswer ? "" : " mc-locked"}`}
                role="radiogroup"
              >
                {options.map(
                  (materialSubKey: string, index: number) => {
                    const learnerAnswerState = !isPreview
                      ? "HIDDEN"
                      : materialSubKey !== displayAnswerMap[materialKey]
                      ? "EMPTY"
                      : checkAnswerState(
                          correctAnswer,
                          displayAnswerMap[materialKey]
                        );
                    const graded = learnerAnswerState !== "HIDDEN";
                    const isCorrectOption = materialSubKey === correctAnswer;
                    const isPickedOption =
                      displayAnswerMap[materialKey] === materialSubKey;
                    const stateClass =
                      (graded
                        ? (learnerAnswerState === "EMPTY" && isCorrectOption) ||
                          learnerAnswerState === "CORRECT"
                          ? " is-correct"
                          : learnerAnswerState === "INCORRECT"
                          ? " is-wrong"
                          : ""
                        : isPickedOption
                        ? " is-sel"
                        : "") +
                      (showLearnerMarks && isPickedOption ? " is-picked" : "") +
                      (gradeAgainstLearner && isCorrectOption && !isPickedOption
                        ? " is-key"
                        : "");
                    const selectOption = () => {
                      if (canAnswer)
                        onChange(answerMap, materialKey, materialSubKey);
                    };
                    return (
                      <div
                        key={index}
                        className={`mc-imgopt${stateClass}`}
                        role="radio"
                        aria-checked={isPickedOption}
                        aria-disabled={!canAnswer}
                        tabIndex={canAnswer ? 0 : -1}
                        onClick={selectOption}
                        onKeyDown={(e) => {
                          // Only keys aimed at the card itself select. A keydown that
                          // bubbles up from the zoom button must pass untouched —
                          // preventDefault here would cancel the button's native
                          // activation and turn keyboard-zoom into an answer.
                          if (e.target !== e.currentTarget) return;
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          selectOption();
                        }}
                      >
                        {/* The image itself SELECTS. Zoom lives on its own button
                            — the convention ShowMaterialMediaByContentType already
                            uses for the question body — because an onClick on the
                            <img> swallowed ~80% of the card: `stopPropagation`
                            meant the picture area could never pick the option, and
                            only the thin label strip below it answered. */}
                        <span className="mc-imgopt__media">
                          <img src={materialSubKey} alt={`option-${index + 1}`} />
                          <button
                            type="button"
                            className="mc-imgopt__zoom"
                            aria-label={i18n.t("activity_zoom_image")}
                            onClick={(e) => {
                              e.stopPropagation();
                              setZoomSrc(materialSubKey);
                            }}
                          >
                            <Icon name="arrow-up" size={12} />
                          </button>
                        </span>
                        <span className="mc-imgopt__label">
                          <span className="mc-dot" aria-hidden="true" />
                          {renderRevealTags(materialSubKey)}
                          {graded &&
                          isCorrectOption &&
                          (gradeAgainstLearner ? isPickedOption : true) ? (
                            <span className="mc-fb mc-fb--ok">✓</span>
                          ) : null}
                          {graded && isPickedOption && !isCorrectOption ? (
                            <span className="mc-fb mc-fb--no">✗</span>
                          ) : null}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
            <div
              className={`mc-options ${
                options.length <= 4
                  ? "mc-options-center"
                  : ""
              }${canAnswer ? "" : " mc-locked"}`}
            >
              {options.map(
                (materialSubKey: string, index: number) => {
                  const learnerAnswerState = !isPreview
                    ? "HIDDEN"
                    : materialSubKey !== displayAnswerMap[materialKey]
                    ? "EMPTY"
                    : checkAnswerState(correctAnswer, displayAnswerMap[materialKey]);
                  const graded = learnerAnswerState !== "HIDDEN";
                  const isCorrectOption = materialSubKey === correctAnswer;
                  const isPickedOption =
                    displayAnswerMap[materialKey] === materialSubKey;

                  return (
                    <div
                      key={index}
                      className={`mc-option mc-option-gap ${
                        (learnerAnswerState === "EMPTY" && isCorrectOption) ||
                        learnerAnswerState === "CORRECT"
                          ? "mc-correct"
                          : learnerAnswerState === "INCORRECT"
                          ? "mc-incorrect"
                          : ""
                      }${
                        learnerAnswerState === "HIDDEN" && isPickedOption
                          ? " mc-option-sel"
                          : ""
                      }${
                        showLearnerMarks && isPickedOption ? " mc-option-picked" : ""
                      }${
                        gradeAgainstLearner && isCorrectOption && !isPickedOption
                          ? " mc-option-key"
                          : ""
                      }`}
                      onClick={() => {
                        if (canAnswer)
                          onChange(answerMap, materialKey, materialSubKey);
                      }}
                    >
                      <span className="mc-dot" aria-hidden="true" />
                      <div className="mc-option-body">
                        {resolveMaterialMediaType(materialSubKey, contentMap.type) === "TEXT" ? (
                          <p className="mc-text">
                            {constructInputWithSpecialExpressionList(
                              materialSubKey
                            ).map((inputPart, index) => (
                              <span
                                key={index}
                                className={`${
                                  inputPart.isBold ? "mc-bold" : ""
                                } ${inputPart.isUnderline ? "mc-underline" : ""}`}
                              >
                                {inputPart.isEquation ? (
                                  <span className="mc-xl">
                                    <InlineMath math={inputPart.value} />
                                  </span>
                                ) : (
                                  inputPart.value
                                )}
                              </span>
                            ))}
                          </p>
                        ) : (
                          <ShowMaterialMediaByContentType
                            key={`${uniqueValue}-${index}`}
                            contentType={resolveMaterialMediaType(materialSubKey, contentMap.type)}
                            src={materialSubKey}
                            canFullScreen={true}
                          />
                        )}
                      </div>
                      {renderRevealTags(materialSubKey)}
                      {graded &&
                      isCorrectOption &&
                      (gradeAgainstLearner ? isPickedOption : true) ? (
                        <span className="mc-fb mc-fb--ok">✓</span>
                      ) : null}
                      {graded && isPickedOption && !isCorrectOption ? (
                        <span className="mc-fb mc-fb--no">✗</span>
                      ) : null}
                    </div>
                  );
                }
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    );
  })}
  {showLegend ? (
    <div className="mc-legend">
      <span className="mc-legend-item">
        <span className="mc-legend-dot is-key" />
        {i18n.t("activity_correct_answer")}
      </span>
      <span className="mc-legend-item">
        <span className="mc-legend-dot is-picked" />
        {i18n.t("activity_your_answer")}
      </span>
    </div>
  ) : null}
  {zoomSrc ? (
    <PortalGlassLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
  ) : null}
  </div>);
};

export default MCSAActivityMaterialContent;
